// Card.js - FIXED version with proper authentication handling for lead capture

import React, { useEffect, useState, useMemo, useRef, useCallback } from 'react';
import { Box, Typography, Avatar, Button, Link as MuiLink } from '@mui/material';
import { useContactLinkContext } from 'contexts/ContactLinkContext';
import placeholderImage from 'images/placeholder-grey-image.jpg';
import avatarPlaceholder from 'images/avatar-cropped.png';
import getIconForType from 'utils/icons/getIconForType';
import { lighten, darken } from '@mui/system';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { getCardById, getDefaultCardByProfileSlug } from 'apis/cardApi';
import TeamLeadCaptureOverlay from 'team/components/editMember/leadCapture/TeamLeadCaptureOverlay';
import { useCardContext } from 'contexts/CardContext';
import TeamNotificationSnackbar from 'team/shared/components/notification/TeamNotificationSnackbar';
import { useAuth } from 'contexts/AuthContext';
import { downloadVCard } from 'utils/vcfUtils';
import { getUserBySlug } from 'apis/userApi';
import { useEventTracking } from 'services/eventTrackingService';
import { API_BASE_URL } from 'utils/apiUtils';
import { ThemeProvider } from '@mui/material/styles';
import { CARD_THEMES } from 'team/shared/themes';
import { getTeamDesignComponent } from 'team/components/cardDesignTemplates/index';

const TeamMemberCard = () => {
    // DECLARE ALL STATE VARIABLES FIRST
    const { contactLinks, setCurrentCard, fetchContactLinksForCard } = useContactLinkContext();
    const location = useLocation();
    const tracking = useEventTracking();
    const navigate = useNavigate();
    const params = useParams();
    const { cardState, loadCardFromServer, isTemporaryCard, updateCardData } = useCardContext();
    const { authState } = useAuth();

    // ALL useState declarations
    const [cardId, setCardId] = useState(null); // DECLARE cardId FIRST
    const [overlayOpen, setOverlayOpen] = useState(false);
    const [cardFullName, setCardFullName] = useState('');
    const [cardJobTitle, setCardJobTitle] = useState('');
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [customLinkTypeLabels, setCustomLinkTypeLabels] = useState({});
    const [cardLocation, setCardLocation] = useState('');
    const [cardBio, setCardBio] = useState('');
    const [cardCompany, setCardCompany] = useState('');
    const [cardTheme, setCardTheme] = useState('');
    const [cardProfilePictureUrl, setCardProfilePictureUrl] = useState('');
    const [cardCoverPhotoUrl, setCardCoverPhotoUrl] = useState('');
    const [cardCompanyLogoUrl, setCardCompanyLogoUrl] = useState('');
    const [contextCardLayout, setContextCardLayout] = useState('center-aligned');
    const [cardLinkColor, setCardLinkColor] = useState('');
    const [cardDetails, setCardDetails] = useState({});
    const [cardPronoun, setCardPronoun] = useState('');
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState('');
    const [debugVisible, setDebugVisible] = useState(false);
    const [debugInfo, setDebugInfo] = useState({});

    // THEN ALL useRef declarations
    const prevCardId = useRef(null);
    const prevLeadCaptureMode = useRef(false);
    const leadCaptureModeStable = useRef(false);
    const cardIdStable = useRef(null);

    // ✅ SAFE FIX: Add refs to prevent infinite loops
    const leadCaptureEvaluatedRef = useRef(false);
    const lastCardStateHashRef = useRef('');
    const lastAuthStateHashRef = useRef('');
    const cardDetailsUpdatedRef = useRef(false);

    // CONSTANTS
    const cardDesign = 'design5FloatingButton';
    const cardDesignTheme = CARD_THEMES[cardDesign];

    const CardDesignComponent = getTeamDesignComponent(cardDesign);
    const TRACKING_LOCK_DURATION = 60000;

    // FIXED: Check if user is authenticated and lead capture should be enabled
    const isAuthenticated = authState?.isAuthenticated || false;
    const hasValidUser = authState?.user?.userId || false;

    // MEMOIZE visibleContactLinks
    const visibleContactLinks = useMemo(() => {
        console.log('🔍 TeamMemberCard: Contact links changed:', contactLinks);
        console.log('🔍 TeamMemberCard: Contact links length:', contactLinks?.length || 0);

        if (contactLinks && Array.isArray(contactLinks)) {
            const normalizedLinks = contactLinks.map(link => ({
                ...link,
                type: link.type?.toLowerCase() || link.type
            }));

            // ✅ FRONTEND-ONLY FIX: Enhanced visibility filtering
            const visibleLinks = normalizedLinks.filter(link => {
                // Check multiple sources for visibility
                const isVisibleFromRoot = link.isVisible;
                const isVisibleFromDetails = link.details?.additionalDetails?.isVisible;

                // Priority: root level > additionalDetails > default true
                let isVisible = true; // default
                if (isVisibleFromRoot !== undefined) {
                    isVisible = isVisibleFromRoot;
                } else if (isVisibleFromDetails !== undefined) {
                    isVisible = isVisibleFromDetails;
                }

                console.log('🔍 TeamMemberCard: Link visibility check:', {
                    linkId: link._id?.substring(0, 8) + '...',
                    type: link.type,
                    isVisibleFromRoot,
                    isVisibleFromDetails,
                    finalIsVisible: isVisible
                });

                return isVisible !== false;
            });

            console.log('🔍 TeamMemberCard: Visible contact links:', visibleLinks);
            console.log('🔍 TeamMemberCard: Visible contact links length:', visibleLinks.length);
            return visibleLinks;
        } else {
            console.log('🔍 TeamMemberCard: No contact links available or not an array');
            return [];
        }
    }, [contactLinks]);

    // STABLE CALLBACK FUNCTIONS
    const handleCloseOverlay = useCallback(() => {
        console.log('🔍 handleCloseOverlay called');
        setOverlayOpen(false);
        leadCaptureModeStable.current = false;
    }, []);

    const handleLeadSuccess = useCallback(() => {
        console.log('🔍 handleLeadSuccess called');
        setSnackbarMessage('Lead captured successfully!');
        setSnackbarOpen(true);
    }, []);

    const handleSnackbarClose = useCallback(() => {
        setSnackbarOpen(false);
    }, []);

    // UPDATE STABLE REFS WHEN CARDID CHANGES
    useEffect(() => {
        if (cardId !== prevCardId.current) {
            prevCardId.current = cardId;
            cardIdStable.current = cardId;
            console.log('🔍 TeamMemberCard ID changed to:', cardId);
        }
    }, [cardId]);

    // ✅ SAFE FIX: Reset refs when cardId changes
    useEffect(() => {
        // Reset all evaluation flags when card changes
        leadCaptureEvaluatedRef.current = false;
        cardDetailsUpdatedRef.current = false;
        lastCardStateHashRef.current = '';
        lastAuthStateHashRef.current = '';
    }, [cardId]);

    // ✅ SAFE FIX: STABILIZE LEAD CAPTURE MODE CALCULATION WITH AUTHENTICATION CHECK
    useEffect(() => {
        // ✅ CRITICAL: Prevent infinite loops with early returns
        if (!cardId || !cardState || leadCaptureEvaluatedRef.current) {
            return;
        }

        try {
            const currentMode = cardState[cardId]?.leadCaptureModeOn ?? false;
            const shouldShowLeadCapture = currentMode && (isAuthenticated || hasValidUser);

            // ✅ CRITICAL: Create stable hash to detect real changes
            const cardStateHash = JSON.stringify({
                leadCaptureMode: currentMode,
                cardId: cardId
            });

            const authStateHash = JSON.stringify({
                isAuthenticated,
                hasValidUser,
                userId: authState?.user?.userId
            });

            // ✅ ONLY update if there are actual changes
            const cardStateChanged = cardStateHash !== lastCardStateHashRef.current;
            const authStateChanged = authStateHash !== lastAuthStateHashRef.current;

            if (cardStateChanged || authStateChanged) {
                console.log('🔍 Lead capture mode evaluation:', {
                    currentMode,
                    isAuthenticated,
                    hasValidUser,
                    shouldShowLeadCapture,
                    authState: !!authState,
                    cardStateChanged,
                    authStateChanged
                });

                if (shouldShowLeadCapture !== prevLeadCaptureMode.current) {
                    console.log('🔍 Lead capture mode changed from', prevLeadCaptureMode.current, 'to', shouldShowLeadCapture);
                    prevLeadCaptureMode.current = shouldShowLeadCapture;
                    leadCaptureModeStable.current = shouldShowLeadCapture;

                    // Use setTimeout to break the render cycle
                    setTimeout(() => {
                        setOverlayOpen(shouldShowLeadCapture);
                    }, 0);
                }

                // ✅ Update hash refs
                lastCardStateHashRef.current = cardStateHash;
                lastAuthStateHashRef.current = authStateHash;
            }

            // ✅ CRITICAL: Mark as evaluated to prevent re-runs
            leadCaptureEvaluatedRef.current = true;

            // ✅ Reset evaluation flag after 30 seconds to allow eventual updates
            setTimeout(() => {
                leadCaptureEvaluatedRef.current = false;
            }, 30000);

        } catch (error) {
            console.error('Error in lead capture evaluation:', error);
        }
    }, [cardId, isAuthenticated, hasValidUser]); // ✅ ONLY essential, stable dependencies

    // MAX LOADING TIMEOUT
    useEffect(() => {
        const maxLoadingTimeout = setTimeout(() => {
            console.warn('Maximum loading time exceeded, forcing loading to false');
            setLoading(false);
            setError('Loading timeout - please refresh the page');
        }, 15000);

        return () => clearTimeout(maxLoadingTimeout);
    }, []);

    // TRACKING FUNCTIONS
    const getTrackingLock = (cardId, source) => {
        const lockKey = `track_lock_${cardId}_${source}`;
        const currentTime = Date.now();
        const existingLock = sessionStorage.getItem(lockKey);

        if (existingLock) {
            const lockTime = parseInt(existingLock, 10);
            if (currentTime - lockTime < TRACKING_LOCK_DURATION) {
                console.log('⚠️ Tracking already performed in the last minute');
                return false;
            }
        }

        sessionStorage.setItem(lockKey, currentTime.toString());
        return true;
    };

    const trackCardViewSafely = async (cardId, source = 'qr_code') => {
        try {
            if (!getTrackingLock(cardId, source)) {
                return { success: true, skipped: true, reason: 'Already tracked recently' };
            }

            let normalizedSource = source;
            if (source === 'direct' || source === 'website' || !source) {
                normalizedSource = 'share_link';
            } else if (source === 'nfc' || source === 'tag') {
                normalizedSource = 'accessory';
            }

            console.log(`🔍 Tracking card view (${normalizedSource}): ${cardId}`);

            let cardOwnerId = null;
            try {
                const response = await fetch(`/api/card/${cardId}`);
                const data = await response.json();
                cardOwnerId = data.card?.userId;
                console.log('Card owner ID:', cardOwnerId);
            } catch (error) {
                console.error('Error getting card owner ID:', error);
            }

            try {
                const response = await fetch(`/api/insights/qr-track`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        cardId,
                        sourceType: normalizedSource,
                        cardOwnerId
                    })
                });

                if (response.ok) {
                    console.log('✅ QR endpoint tracking successful');
                    return { success: true, method: 'qr-track-endpoint' };
                }
            } catch (error) {
                console.warn('QR endpoint failed:', error);
            }

            if (window.eventTrackingService) {
                try {
                    window.eventTrackingService.trackCardViewViaHTTP(cardId, normalizedSource);
                    console.log('✅ Direct HTTP tracking successful');
                    return { success: true, method: 'eventTrackingService-HTTP' };
                } catch (error) {
                    console.warn('EventTrackingService HTTP tracking failed:', error);
                }
            }

            if (tracking) {
                try {
                    tracking.trackCardView(cardId, normalizedSource);
                    console.log('✅ Component tracking successful');
                    return { success: true, method: 'component-tracking' };
                } catch (error) {
                    console.warn('Component tracking failed:', error);
                }
            }

            try {
                const response = await fetch(`/api/event-log/create`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        userId: cardOwnerId || 'anonymous',
                        entityId: cardId,
                        entityType: 'card',
                        eventType: 'card_view',
                        sourceType: normalizedSource,
                        isAnonymous: true,
                        timestamp: new Date().toISOString()
                    })
                });

                if (response.ok) {
                    console.log('✅ Direct event log API tracking successful');
                    return { success: true, method: 'direct-event-log-api' };
                }
            } catch (error) {
                console.error('All tracking methods failed:', error);
            }

            return { success: false, error: 'All tracking methods failed' };
        } catch (error) {
            console.error('Critical tracking error:', error);
            return { success: false, error: error.message };
        }
    };

    // TRACKING EFFECT
    useEffect(() => {
        if (!cardId || loading) return;

        const urlParams = new URLSearchParams(window.location.search);
        const source = urlParams.get('source') || '';
        const trackParam = urlParams.get('track');

        if (trackParam === 'false') {
            console.log('🚫 Tracking disabled for preview view (track=false)');
            return;
        }

        const serialParam = urlParams.get('accessorySerial') || urlParams.get('serial');

        console.log('Card view tracking info:', {
            source,
            serialParam,
            cardId,
            trackParam
        });

        if (source) {
            console.log(`🔍 Tracking card view with source type: ${source}`);

            if (source === 'accessory') {
                console.log('Tracking accessory view with:', {
                    serialNumber: serialParam,
                    cardId
                });

                if (serialParam) {
                    tracking.trackAccessoryView({
                        cardId,
                        serialNumber: serialParam,
                        sourceType: 'accessory',
                        metadata: {
                            accessorySerialNumber: serialParam,
                            exactSerialNumber: serialParam,
                            source: 'accessory_scan',
                            isAccessoryScan: true
                        }
                    });
                } else {
                    const referrerMatch = document.referrer.match(/\/accessory\/scan\/([a-zA-Z0-9]+)/);
                    const serialFromReferrer = referrerMatch ? referrerMatch[1] : null;

                    if (serialFromReferrer) {
                        console.log('Using serial from referrer:', serialFromReferrer);
                        tracking.trackAccessoryView({
                            cardId,
                            serialNumber: serialFromReferrer,
                            sourceType: 'accessory',
                            metadata: {
                                accessorySerialNumber: serialFromReferrer,
                                exactSerialNumber: serialFromReferrer,
                                source: 'accessory_scan',
                                isAccessoryScan: true,
                                fromReferrer: true
                            }
                        });
                    } else {
                        console.log('No serial number found, tracking without it');
                        tracking.trackAccessoryView({
                            cardId,
                            sourceType: 'accessory',
                            metadata: {
                                source: 'accessory_scan',
                                isAccessoryScan: true,
                                noSerial: true
                            }
                        });
                    }
                }
            } else {
                console.log(`Tracking card view with source: ${source}`);
                trackCardViewSafely(cardId, source);
            }
        } else {
            console.log('No source parameter found, tracking as direct view');
            trackCardViewSafely(cardId, 'direct');
        }
    }, [cardId, loading, tracking]);

    // SETUP CARD ID
    useEffect(() => {
        const setupCardId = async () => {
            try {
                if (location.pathname.includes('/profile/')) {
                    const profileIdentifier = params.profileId;
                    if (!profileIdentifier) {
                        console.error('No profile identifier found');
                        setError('Profile not found');
                        setLoading(false);
                        return;
                    }

                    try {
                        console.log('Fetching user by slug:', profileIdentifier);
                        const userResponse = await getUserBySlug(profileIdentifier);
                        console.log('User response:', userResponse);

                        if (userResponse.success) {
                            try {
                                console.log('Fetching default card for user:', userResponse.userId);

                                let lastError = null;
                                const maxRetries = 3;
                                const baseDelay = 1000;

                                for (let attempt = 0; attempt <= maxRetries; attempt++) {
                                    try {
                                        console.log(`🔍 Card: Attempt ${attempt + 1}/${maxRetries + 1} to fetch default card for user:`, userResponse.userId);

                                        const defaultCard = await getDefaultCardByProfileSlug(profileIdentifier);
                                        console.log('Default card response:', defaultCard);

                                        if (defaultCard && defaultCard._id) {
                                            setCardId(defaultCard._id);
                                            console.log("Setting card ID to:", defaultCard._id);
                                            return;
                                        } else {
                                            console.error("Default card doesn't have a valid _id:", defaultCard);
                                            setError('No card found for this profile');
                                            setLoading(false);
                                            return;
                                        }
                                    } catch (error) {
                                        lastError = error;
                                        console.log(`🔍 Card: Attempt ${attempt + 1} failed:`, error.message);

                                        if (attempt === maxRetries) {
                                            break;
                                        }

                                        const delay = baseDelay * Math.pow(2, attempt);
                                        console.log(`🔍 Card: Waiting ${delay}ms before retry...`);
                                        await new Promise(resolve => setTimeout(resolve, delay));
                                    }
                                }

                                console.error('🔍 Card: All retry attempts failed for fetching default card:', lastError);
                                setError('Error loading card data');
                                setLoading(false);
                            } catch (error) {
                                console.error('Error fetching default card:', error);
                                setError('Error loading card data');
                                setLoading(false);
                            }
                        } else {
                            console.error('User not found for profile:', profileIdentifier);
                            setError('Profile not found');
                            setLoading(false);
                        }
                    } catch (error) {
                        console.error('Error fetching user data:', error);
                        setError('Error loading profile data');
                        setLoading(false);
                    }
                } else {
                    if (params.cardId) {
                        setCardId(params.cardId);
                    } else {
                        console.error('No card ID found in params');
                        setError('Card not found');
                        setLoading(false);
                    }
                }
            } catch (error) {
                console.error('Critical error in setupCardId:', error);
                setError('Failed to load card');
                setLoading(false);
            }
        };

        setupCardId();
    }, [location.pathname, params]);

    useEffect(() => {
        if (cardId) {
            console.log("card id is ", cardId);
        }
    }, [cardId]);

    // SET CURRENT CARD AND FETCH CONTACT LINKS
    useEffect(() => {
        if (cardId) {
            console.log('🔍 TeamMemberCard: Setting current card:', cardId);
            console.log('🔍 TeamMemberCard: CardId type:', typeof cardId);
            console.log('🔍 TeamMemberCard: CardId length:', cardId?.length);
            console.log('🔍 TeamMemberCard: Is MongoDB ObjectId:', cardId?.length === 24 && /^[0-9a-fA-F]{24}$/.test(cardId));

            setCurrentCard(cardId);

            console.log('🔍 TeamMemberCard: Fetching contact links for cardId:', cardId);
            fetchContactLinksForCard(cardId);
        }
    }, [cardId, setCurrentCard, fetchContactLinksForCard]);

    // FETCH CARD DETAILS
    useEffect(() => {
        const fetchCardDetails = async () => {
            try {
                console.log('Fetching card details for cardId:', cardId);

                const timeoutPromise = new Promise((_, reject) =>
                    setTimeout(() => reject(new Error('Request timeout')), 10000)
                );

                const fetchPromise = getCardById(cardId);

                const data = await Promise.race([fetchPromise, timeoutPromise]);

                if (data && data.success !== false) {
                    setCardDetails(data);

                    if (!isTemporaryCard(cardId)) {
                        await loadCardFromServer(cardId);
                    }

                    console.log('Card Details fetched successfully:', data);
                } else {
                    console.error('Invalid card data received:', data);
                    setError('Card data is invalid');
                }

                setLoading(false);
            } catch (error) {
                console.error('Error fetching card details:', error);
                setLoading(false);
            }
        };

        if (cardId) {
            fetchCardDetails();
        } else if (cardId === null) {
            console.log('CardId is null, stopping loading');
            setLoading(false);
        }
    }, [cardId]);

    // ✅ SAFE FIX: UPDATE CARD DETAILS
    useEffect(() => {
        // ✅ CRITICAL: Prevent multiple updates of the same card details
        if (!cardDetails?.card || cardDetailsUpdatedRef.current) {
            return;
        }

        try {
            console.log('🔍 Card details updated - lead capture mode from server:', cardDetails.card.leadCaptureModeOn);
            console.log('🔍 Authentication state:', { isAuthenticated, hasValidUser });

            // ✅ Update all card state at once
            setCardFullName(cardDetails.card.contact_info?.fullName || '');
            setCardJobTitle(cardDetails.card.contact_info?.jobTitle || '');
            setCardCompany(cardDetails.card.contact_info?.company || '');
            setCardPronoun(cardDetails.card.contact_info?.pronoun || 'he');
            setCustomLinkTypeLabels(cardDetails.card.customLinkTypeLabels || {});
            setCardTheme(cardDetails.card.cardTheme || '');
            setCardProfilePictureUrl(cardDetails.card.profilePictureUrl || '');
            setCardCoverPhotoUrl(cardDetails.card.coverPhotoUrl || '');
            setCardCompanyLogoUrl(cardDetails.card.companyLogoUrl || '');
            setContextCardLayout(cardDetails.card.cardLayout || 'center-aligned');
            setCardLinkColor(cardDetails.card.linkColor || '');
            setCardLocation(cardDetails.card.contact_info?.location || '');
            setCardBio(cardDetails.card.bio || '');

            // ✅ Update context with setTimeout to break render cycle
            if (updateCardData && cardId) {
                setTimeout(() => {
                    updateCardData(cardId, {
                        leadCaptureModeOn: Boolean(cardDetails.card.leadCaptureModeOn),
                        autoFollowUpIntroEmail: Boolean(cardDetails.card.autoFollowUpIntroEmail),
                        leadCaptureInputFields: cardDetails.card.leadCaptureInputFields || [] // Update input fields including dropdowns
                    }, false);
                }, 0);
            }

            // ✅ CRITICAL: Mark as updated to prevent re-runs
            cardDetailsUpdatedRef.current = true;

            // ✅ Reset flag when cardDetails change (new card loaded)
            const cardIdFromDetails = cardDetails.card._id || cardDetails.card.cardId;
            if (cardIdFromDetails !== cardId) {
                cardDetailsUpdatedRef.current = false;
            }

        } catch (error) {
            console.error('Error updating card details:', error);
        }
    }, [cardDetails, cardId]); // ✅ ONLY essential dependencies

    // LOGGING EFFECT
    useEffect(() => {
        console.log('Full Name:', cardFullName);
        console.log('Job Title:', cardJobTitle);
        console.log('Company:', cardCompany);
        console.log('Theme:', cardTheme);
        console.log('Profile Picture URL:', cardProfilePictureUrl);
        console.log('Cover Photo URL:', cardCoverPhotoUrl);
        console.log('Company Logo URL:', cardCompanyLogoUrl);
        console.log('Layout:', contextCardLayout);
        console.log('Link Color:', cardLinkColor);
    }, [cardFullName, cardJobTitle, cardCompany, cardTheme, cardProfilePictureUrl, cardCoverPhotoUrl, cardCompanyLogoUrl, contextCardLayout, cardLinkColor]);

    // SAVE CONTACT HANDLER
    const handleSaveContact = async () => {
        const cardData = {
            contact_info: {
                fullName: cardFullName,
                phoneNumber: cardDetails?.card?.contact_info?.phoneNumber,
                email: cardDetails?.card?.contact_info?.email,
                jobTitle: cardJobTitle,
                company: cardCompany,
                location: cardLocation
            },
            bio: cardBio,
            profilePictureUrl: cardProfilePictureUrl
        };

        downloadVCard(cardData);

        console.log("Save Contact button clicked - tracking download event");

        const cardOwnerId = cardDetails?.card?.userId;
        console.log("Tracking contact download for user:", cardOwnerId, "card:", cardId);

        const payload = {
            cardId: cardId,
            sourceType: 'card',
            isAnonymous: true,
            eventType: 'contact_download',
            metadata: {
                cardId: cardId,
                cardHolderName: cardFullName,
                userName: 'Anonymous',
                action: 'contact_download',
                downloadedData: {
                    fullName: cardFullName,
                    email: cardDetails?.card?.contact_info?.email,
                    jobTitle: cardJobTitle,
                    company: cardCompany,
                    phoneNumber: cardDetails?.card?.contact_info?.phoneNumber,
                    location: cardLocation
                },
                timestamp: new Date().toISOString()
            }
        };

        console.log("Contact download payload:", payload);

        try {
            const endpoints = [
                `${API_BASE_URL}/api/insights/track-view`,
                `${API_BASE_URL}/api/event-log/create`,
                '/api/insights/track-view',
                '/api/event-log/create'
            ];

            let success = false;
            for (const endpoint of endpoints) {
                try {
                    const response = await fetch(endpoint, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify(payload)
                    });

                    if (response.ok) {
                        const responseData = await response.json();
                        console.log("✅ Contact download tracked successfully via:", endpoint, responseData);
                        success = true;
                        break;
                    }
                } catch (endpointError) {
                    console.warn(`❌ Endpoint ${endpoint} failed:`, endpointError.message);
                }
            }

            if (!success) {
                console.error("❌ All contact download tracking endpoints failed");
            }
        } catch (error) {
            console.error("❌ Contact download tracking critical error:", error);
        }
    };

    const formatPronounDisplay = (pronoun) => {
        switch (pronoun) {
            case 'he': return 'he/him';
            case 'she': return 'she/her';
            case 'they': return 'they/them';
            case 'ze': return 'ze/zir';
            case '': return '';
            case 'custom': return 'custom';
            default:
                return pronoun;
        }
    };

    if (loading) {
        return <div>Loading...</div>;
    }

    if (!cardDetails) {
        return <div>No card details available</div>;
    }

    return (
        <ThemeProvider theme={cardDesignTheme}>
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    width: '100%',
                    margin: 'auto',
                    boxSizing: 'border-box',
                    padding: '20px',
                    minHeight: '100vh',
                    overflowX: 'hidden',
                    position: 'relative'
                }}
            >
                <TeamNotificationSnackbar
                    open={snackbarOpen}
                    message={snackbarMessage}
                    handleClose={handleSnackbarClose}
                />

                {debugVisible && (
                    <Box
                        sx={{
                            position: 'fixed',
                            top: 0,
                            left: 0,
                            width: '100%',
                            backgroundColor: 'rgba(0,0,0,0.9)',
                            color: 'white',
                            zIndex: 9999,
                            padding: '15px',
                            fontSize: '12px',
                            textAlign: 'left'
                        }}
                    >
                        <Typography variant="h6" gutterBottom>QR Code Tracking Debug</Typography>
                        <Box sx={{ mb: 2 }}>
                            <Typography color={debugInfo.result?.success ? 'success.main' : 'error.main'} sx={{ mb: 1 }}>
                                {debugInfo.trackingComplete
                                    ? (debugInfo.result?.success
                                        ? `✅ TRACKING SUCCESS (via ${debugInfo.result.method})`
                                        : '❌ TRACKING FAILED')
                                    : '⏳ Tracking in progress...'}
                            </Typography>
                            {debugInfo.result?.skipped && (
                                <Typography color="warning.main">
                                    ⚠️ Tracking skipped: {debugInfo.result.reason}
                                </Typography>
                            )}
                        </Box>
                        <pre>{JSON.stringify(debugInfo, null, 2)}</pre>
                        <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
                            <Button
                                variant="contained"
                                color="primary"
                                size="small"
                                onClick={() => trackCardViewSafely(cardId, 'manual_debug')}
                            >
                                Force Track Now
                            </Button>
                            <Button
                                variant="contained"
                                color="secondary"
                                size="small"
                                onClick={() => window.location.reload()}
                            >
                                Reload Page
                            </Button>
                            <Button
                                variant="contained"
                                color="warning"
                                size="small"
                                onClick={() => setDebugVisible(false)}
                            >
                                Close
                            </Button>
                        </Box>
                    </Box>
                )}

                {/* FIXED: LeadCaptureOverlay with authentication check and proper error handling */}
                {overlayOpen && (isAuthenticated || hasValidUser) && (
                    <TeamLeadCaptureOverlay
                        key="stable-lead-capture"
                        onClose={handleCloseOverlay}
                        onSuccess={handleLeadSuccess}
                        addMoreConnectButtonColor={cardLinkColor || '#2f82ed'}
                        triggerSource="auto"
                    />
                )}

                {/* FIXED: Show warning if lead capture would be triggered but user is not authenticated */}
                {overlayOpen && !isAuthenticated && !hasValidUser && (
                    <Box
                        sx={{
                            position: 'fixed',
                            top: '50%',
                            left: '50%',
                            transform: 'translate(-50%, -50%)',
                            backgroundColor: 'rgba(255, 193, 7, 0.1)',
                            border: '1px solid rgba(255, 193, 7, 0.3)',
                            borderRadius: '8px',
                            padding: '16px',
                            maxWidth: '300px',
                            zIndex: 1000,
                            textAlign: 'center'
                        }}
                    >
                        <Typography variant="body2" color="warning.main">
                            Lead capture is enabled for this card, but requires user authentication.
                        </Typography>
                        <Button
                            onClick={handleCloseOverlay}
                            sx={{ mt: 1 }}
                            size="small"
                        >
                            Continue Viewing Card
                        </Button>
                    </Box>
                )}

                {!loading ? (
                    <CardDesignComponent
                        cardId={cardId}
                        cardFullName={cardFullName}
                        cardJobTitle={cardJobTitle}
                        cardCompany={cardCompany}
                        cardTheme={cardTheme}
                        cardPronoun={cardPronoun}
                        formattedPronoun={formatPronounDisplay(cardPronoun)}
                        cardProfilePictureUrl={cardProfilePictureUrl}
                        cardCoverPhotoUrl={cardCoverPhotoUrl}
                        cardCompanyLogoUrl={cardCompanyLogoUrl}
                        contextCardLayout={contextCardLayout}
                        cardLinkColor={cardLinkColor}
                        cardLocation={cardLocation}
                        cardBio={cardBio}
                        visibleContactLinks={visibleContactLinks}
                        handleSaveContact={handleSaveContact}
                        showLeadCaptureAuto={false}
                        customLinkTypeLabels={customLinkTypeLabels}
                    />
                ) : (
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            height: '70vh',
                            width: '100%'
                        }}
                    >
                        <Typography>Loading...</Typography>
                    </Box>
                )}
            </Box>
        </ThemeProvider>
    );
};

export default TeamMemberCard;